import { associationIdName, fetchGet, fetchPost, siteNumber } from './common';
import { getUser } from './users';
import { stripHtml } from '../lib/stripHtml';

function buildAssociationSiteQuery(): string {
    let query = `association=${associationIdName}`;
    if (siteNumber) {
        query += `&siteNumber=${siteNumber}`;
    }
    return query;
}

/**
 * Legacy mutation routes use `Events` (capital E), matching VoterVoiceAPI.js and
 * `Api/Events/{id}/Registration` in ApiAreaRegistration.
 *
 * Casing does NOT decide which backend answers on deployed environments: ASP.NET
 * MVC routing and the IIS rewrite are both case-insensitive. Where it does matter
 * is the **local Vite proxy**, whose keys are case-sensitive prefixes — lowercase
 * `/Api/events*` goes to the local REST host on 5001, while anything else under
 * `/Api` falls through to the remote dev UserSite. So this capital prefix is what
 * keeps register/cancel on legacy UserSite during local development while the
 * lowercase list/detail calls exercise the REST host.
 */
const EVENTS_MUTATION_PREFIX = 'Events';

/** Legacy UserSite Events API authenticates via `?user={token}` (see VoterVoiceAPI.js). */
function appendUserToQuery(baseQuery: string, userToken?: string): string {
    const token = userToken?.trim();
    if (!token) return baseQuery;
    return `${baseQuery}&user=${encodeURIComponent(token)}`;
}

/** Resolve the access token used for POST/DELETE registration (query `user` param). */
/**
 * Remember-me cookie is the source of truth on deployed sites (same as Events Index).
 * vvSettings.userContainer.userToken is only a fallback for early load / dev.
 */
export async function resolveEventUserToken(): Promise<string | undefined> {
    try {
        const remembered = await getUser();
        const token = remembered?.userToken?.trim();
        if (token) return token;
    } catch {
        // GetRemembered may fail without cookies (localhost proxy).
    }
    return getEventUserUrlToken();
}

export function resolveRegistrationUserToken(
    request: Pick<RegisterEventRequest, 'userToken'>,
    ...extraTokens: Array<string | undefined>
): string {
    const candidates = [request.userToken, ...extraTokens, getEventUserUrlToken()];
    for (const candidate of candidates) {
        if (typeof candidate !== 'string') continue;
        const trimmed = candidate.trim();
        if (trimmed.length > 0 && !PLACEHOLDER_USER_TOKENS.has(trimmed)) {
            return trimmed;
        }
    }
    return '';
}

/** Map Svelte form values to legacy Events/register.cshtml option values. */
function mapLegacyPreferredContactMethod(method: string | undefined): string | undefined {
    if (!method) return undefined;
    const normalized = method.trim().toLowerCase();
    if (normalized === 'email') return 'Email';
    if (normalized === 'phone' || normalized === 'cell phone') return 'Cell Phone';
    if (normalized === 'text') return 'Text';
    if (normalized === 'either') return 'Email';
    if (method === 'Email' || method === 'Cell Phone' || method === 'Text') return method;
    return 'Email';
}

/**
 * JSON body for POST …/Registration. Mirrors Scripts/Events.Register.js
 * `getEventRegistration()` so legacy UserSite model binding + JsonContractDefinitionBinder
 * receive the same field set as the MVC register page. Auth token travels in `?user=`
 * only (VoterVoiceAPI.js), not in the body.
 */
function buildRegistrationPostBody(request: RegisterEventRequest): Record<string, unknown> {
    const country = request.country || 'US';

    const body: Record<string, unknown> = {
        eventId: request.eventId,
        firstName: request.firstName,
        lastName: request.lastName,
        email: request.email,
        phoneNumber: request.phone ?? '',
        cellPhoneNumber: request.mobileNumber ?? '',
        faxNumber: request.fax ?? '',
        homeAddress: {
            streetAddress: request.address ?? '',
            city: request.city ?? '',
            state: request.state ?? '',
            zipCode: request.zip ?? '',
            country,
        },
        businessAddress: {
            streetAddress: request.businessStreet ?? '',
            city: request.businessCity ?? '',
            state: request.businessState ?? '',
            zipCode: request.businessZip ?? '',
            country,
        },
        companyName: request.businessName ?? '',
        companyTitle: request.businessTitle ?? '',
    };

    const preferredContactMethod = mapLegacyPreferredContactMethod(request.preferredContactMethod);
    if (preferredContactMethod) {
        body.preferredContactMethod = preferredContactMethod;
    }

    if (request.specialNeeds) {
        body.specialNeeds = request.specialNeeds;
    }
    if (request.notes) {
        body.notes = request.notes;
    }
    if (request.includeFirstTimeAttendee) {
        body.firstTimeAttendee = request.firstTimeAttendee;
    }
    if (request.attendeeType) {
        body.attendeeType = request.attendeeType;
    }

    return body;
}

/**
 * Localhost-only placeholder values that ship with the dev example vvSettings
 * (see Svelte/examples/vvSettings.js). Forwarding any of these to the backend
 * always fails authentication and just pollutes auth logs / share-attribution
 * records, so we treat them as "no token" and let callers fall through to the
 * anonymous code path.
 */
const PLACEHOLDER_USER_TOKENS = new Set<string>([
    'mock-user-token-12345'
]);

/**
 * Reads the server-injected URL token for the current user (set via remember-me /
 * single-sign-on). Mirrors getPetitionUserUrlToken() so register/cancel calls can
 * identify the user against the legacy /Api/Events endpoints, which accept the
 * token via ?user={token}.
 *
 * Returns undefined when vvSettings carries one of the well-known localhost
 * placeholders -- those aren't real tokens, so emitting them as ?user=... would
 * just produce phony anonymous attempts and (for share URLs) bogus Action
 * Center attribution.
 */
export function getEventUserUrlToken(): string | undefined {
    const token = (window as any).vvSettings?.userContainer?.userModel?.userToken;
    if (typeof token !== 'string') return undefined;
    const trimmed = token.trim();
    if (trimmed.length === 0) return undefined;
    if (PLACEHOLDER_USER_TOKENS.has(trimmed)) return undefined;
    return trimmed;
}

/**
 * Local-development mocks. When `vvSettings.events` is provided (e.g. via
 * examples/Events/eventsSettings.js) every API function short-circuits to this
 * fixture so the design can be iterated on without a backend. In production
 * `vvSettings.events` is absent and the real fetch path runs.
 */
function getMockEvents(): EventsResponse | undefined {
    const mock = (window as any).vvSettings?.events as EventsResponse | undefined;
    if (!mock) return undefined;
    return {
        spotlightEvents: (mock.spotlightEvents ?? []).map(normalizeEvent),
        otherEvents: (mock.otherEvents ?? []).map(normalizeEvent)
    };
}

function findMockEvent(id: number): Event | undefined {
    const mock = getMockEvents();
    if (!mock) return undefined;
    return [...mock.spotlightEvents, ...mock.otherEvents].find(e => e.id === id);
}

function withUserToken(baseQuery: string, userToken?: string): string {
    if (!userToken) return baseQuery;
    return `${baseQuery}&user=${encodeURIComponent(userToken)}`;
}

/**
 * Shape returned by GET /Api/Events and GET /Api/Events/{id}.
 *
 * Optional fields here are either domain-optional (e.g. location) or only on some
 * backends (e.g. eventDisplayDate on legacy MVC list payloads). Prefer
 * {@link formatEventScheduleDisplay} in UI so startDate/endDate work everywhere.
 *
 * NOTE: registration cap fields (maxRegistrations, registrationCount,
 * isRegistrationFull, availableSlots) are populated when the backend supports
 * the Event.MaxRegistrations column; UI treats them as optional for rollout.
 */
export interface Event {
    id: number;
    /**
     * Legacy alias for {@link id}. The REST reads no longer emit it, but this stays on the
     * interface because `normalizeEvent` still reads it: some legacy payloads carry a
     * default `id: 0` next to a valid `eventId`, and that fallback is the only thing that
     * keeps registration working for them. Read ids via {@link resolveEventId}.
     */
    eventId?: number;
    title: string;
    alert?: string;
    description: string;
    eventDisplayDate?: string;
    startDate?: string;
    endDate?: string;
    eventTimeZone?: string;
    isAllDayEvent?: boolean;
    /** Optional venue/address set in AdminSite; included in calendar exports when present. */
    location?: string;
    /** Maximum active registrations when admin sets a cap; absent/null means unlimited. */
    maxRegistrations?: number | null;
    /** Active (non-canceled) registration count from the server. */
    registrationCount?: number;
    /** True when the cap is reached and new registrations should be blocked. */
    isRegistrationFull?: boolean;
    /** Remaining slots when a cap is set; null/undefined when unlimited. */
    availableSlots?: number | null;
    imageUrl?: string;
    isSpotlight?: boolean;
    /** Server-side flag. The list endpoint also populates {@link isRegistered}. */
    spotlighted?: boolean;
    priority?: number;
    hideAlert?: boolean;
    isRegistered: boolean;
    accommodations?: string;
    travelDetails?: string;
    program?: string;
    resources?: string;
    /**
     * Mirrors the legacy `DisplaySettings.OtherRegistrationInformationVisible` per-event
     * admin toggle. When true (or when the legacy admin has opted in), the register form
     * exposes the "First Time Attendee" + "Special Needs" fields. Default is false so we
     * match the out-of-the-box legacy behaviour where those fields stay hidden.
     */
    showOtherRegistrationInformation?: boolean;
    /**
     * Per-event custom thank-you message shown on the confirmation page (HTML allowed).
     * Mirrors `ConfirmationDisplaySettings.ThankYouMessage` on the legacy MVC backend
     * (see Areas/Events/Controllers/EventsController.ConfirmRegistration). Petitions use
     * an analogous `petition.confirmationMessage` field. When absent we fall back to the
     * generic `t.Thank_You_Default()` copy.
     */
    thankYouMessage?: string;

    /**
     * Whether the "Share with friends" section should be shown on the confirmation page.
     * Mirrors `ConfirmationDisplaySettings.ShowTellAFriendLink` (REST-side: combined
     * Event.ShowTellAFriendLink AND association-default). When the field is absent
     * (legacy backend, mock fixtures) the UI defaults to showing the section so we
     * don't accidentally hide it for backends that haven't plumbed the flag yet.
     */
    sharingEnabled?: boolean;
}

export interface EventsResponse {
    spotlightEvents: Event[];
    otherEvents: Event[];
}

export interface EventRegistration {
    eventId: number;
    event: Event;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    mobileNumber?: string;
    fax?: string;
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
    businessStreet?: string;
    businessCity?: string;
    businessState?: string;
    businessZip?: string;
    businessName?: string;
    businessTitle?: string;
    preferredContactMethod: 'email' | 'phone' | 'either';
    specialNeeds?: string;
    notes?: string;
    firstTimeAttendee: boolean;
    isRegistered: boolean;
}

export interface RegisterEventRequest {
    eventId: number;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    mobileNumber?: string;
    fax?: string;
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
    businessStreet?: string;
    businessCity?: string;
    businessState?: string;
    businessZip?: string;
    businessName?: string;
    businessTitle?: string;
    country?: string;
    preferredContactMethod: string;
    specialNeeds?: string;
    notes?: string;
    firstTimeAttendee: boolean;
    /** When false, omit firstTimeAttendee from POST (field hidden on legacy form). */
    includeFirstTimeAttendee?: boolean;
    attendeeType?: string;
    rememberMe?: boolean;
    /**
     * Used only to build `?user=` on the URL — not sent in the registration JSON body.
     */
    userToken?: string;
}

export interface EventRegistrationContract {
    success: boolean;
    message?: string;
    calendarUrls?: CalendarUrls;
}

export interface CalendarUrls {
    google: string;
    apple: string;
    outlook: string;
    yahoo: string;
}

// Re-export for calendar URL helpers and other callers.
export { stripHtml };

/** Plain-text card excerpt from rich HTML description (headings/paragraphs get spaces). */
export function getEventDescriptionExcerpt(html: string | undefined, maxLength: number = 200): string {
    const text = stripHtml(html);
    if (!text) return '';
    if (text.length <= maxLength) return text;
    const truncated = text.substring(0, maxLength).replace(/\s+\S*$/, '');
    return `${truncated}...`;
}

// Helper function to calculate days until event
export function getDaysUntilEvent(startDate: string | undefined): number {
    if (!startDate) return Number.NaN;
    const eventDate = new Date(startDate);
    if (Number.isNaN(eventDate.getTime())) return Number.NaN;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    eventDate.setHours(0, 0, 0, 0);
    const diffTime = eventDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
}

// Helper function to check if event is starting soon (within threshold)
export function isStartingSoon(startDate: string | undefined, thresholdDays: number = 14): boolean {
    const days = getDaysUntilEvent(startDate);
    return !Number.isNaN(days) && days >= 0 && days <= thresholdDays;
}

// Helper function to check if event has passed
export function hasEventPassed(startDate: string | undefined): boolean {
    const days = getDaysUntilEvent(startDate);
    return !Number.isNaN(days) && days < 0;
}

// Get friendly time label
export function getFriendlyTimeLabel(startDate: string | undefined): { label: string; type: 'urgent' | 'soon' | 'upcoming' } | null {
    const days = getDaysUntilEvent(startDate);

    if (Number.isNaN(days)) return null;
    if (days < 0) return null; // Event has passed
    if (days === 0) return { label: 'Today!', type: 'urgent' };
    if (days === 1) return { label: 'Tomorrow!', type: 'urgent' };
    if (days <= 7) return { label: `In ${days} days`, type: 'soon' };
    if (days <= 14) return { label: `In ${days} days`, type: 'upcoming' };
    
    return null; // Beyond threshold
}

/**
 * Pins an all-day event's date to UTC midnight so it can be read as a plain
 * calendar day everywhere downstream.
 */
function toUtcCalendarDay(iso: string | undefined): string | undefined {
    if (!iso) return iso;
    const datePart = /^(\d{4}-\d{2}-\d{2})/.exec(iso)?.[1];
    return datePart ? `${datePart}T00:00:00Z` : iso;
}

function parseEventInstant(iso: string | undefined): Date | undefined {
    if (!iso?.trim()) return undefined;
    const parsed = new Date(iso);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
}

function calendarDayKey(date: Date, timeZone?: string): string {
    return date.toLocaleDateString('en-CA', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        ...(timeZone ? { timeZone } : {}),
    });
}

/**
 * `eventTimeZone` is set by an admin and validated against NodaTime's tzdb on
 * save, so it should always be an IANA name Intl accepts. It still reaches us
 * as raw server data, and Intl throws RangeError on anything it can't parse --
 * which would take out the whole schedule line instead of degrading it. Fall
 * back to the viewer's local zone in that case.
 */
function usableTimeZone(timeZone: string | undefined): string | undefined {
    const trimmed = timeZone?.trim();
    if (!trimmed) return undefined;
    try {
        new Intl.DateTimeFormat(undefined, { timeZone: trimmed });
        return trimmed;
    } catch {
        return undefined;
    }
}

/**
 * Human-readable schedule for hero, confirmation summary, register page, etc.
 * Uses server-built {@link Event.eventDisplayDate} when present (legacy MVC list cards);
 * otherwise formats {@link Event.startDate} / {@link Event.endDate} from RestApi or
 * UserSite `/Api/Events` (which expose the event timeframe, not a display string).
 *
 * Timed events render in the event's own timezone, not the viewer's, so every
 * surface shows the same clock time the admin scheduled.
 */
export function formatEventScheduleDisplay(
    event: Pick<Event, 'eventDisplayDate' | 'startDate' | 'endDate' | 'isAllDayEvent' | 'eventTimeZone'>
): string | undefined {
    const preset = event.eventDisplayDate?.trim();
    if (preset) return preset;

    const start = parseEventInstant(event.startDate);
    if (!start) return undefined;

    const end = parseEventInstant(event.endDate);
    // All-day events must always be read in UTC.
    const timeZone = event.isAllDayEvent ? 'UTC' : usableTimeZone(event.eventTimeZone);

    const dateOptions: Intl.DateTimeFormatOptions = {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        ...(timeZone ? { timeZone } : {}),
    };
    const timeOptions: Intl.DateTimeFormatOptions = {
        hour: 'numeric',
        minute: '2-digit',
        ...(timeZone ? { timeZone, timeZoneName: 'short' } : { timeZoneName: 'short' }),
    };

    const formatDate = (d: Date) => d.toLocaleDateString(undefined, dateOptions);
    const formatTime = (d: Date) => d.toLocaleTimeString(undefined, timeOptions);

    if (event.isAllDayEvent) {
        const startLabel = formatDate(start);
        if (!end || calendarDayKey(start, timeZone) === calendarDayKey(end, timeZone)) {
            return startLabel;
        }
        return `${startLabel} - ${formatDate(end)}`;
    }

    const startDateLabel = formatDate(start);
    const startTimeLabel = formatTime(start);

    if (!end) {
        return `${startDateLabel} • ${startTimeLabel}`;
    }

    if (calendarDayKey(start, timeZone) === calendarDayKey(end, timeZone)) {
        return `${startDateLabel} • ${startTimeLabel} – ${formatTime(end)}`;
    }

    return `${startDateLabel} • ${startTimeLabel} – ${formatDate(end)} • ${formatTime(end)}`;
}

/** Calendar day of `date` read in UTC. `separator` of '-' yields the extended
 *  form (`2026-09-16`) some providers require; the default is basic (`20260916`). */
function formatUtcDateOnly(date: Date, separator = ''): string {
    const y = date.getUTCFullYear();
    const m = String(date.getUTCMonth() + 1).padStart(2, '0');
    const d = String(date.getUTCDate()).padStart(2, '0');
    return [y, m, d].join(separator);
}

function addUtcDays(date: Date, days: number): Date {
    const result = new Date(date.getTime());
    result.setUTCDate(result.getUTCDate() + days);
    return result;
}

// Generate calendar URLs for an event
export function generateCalendarUrls(event: Event): CalendarUrls {
    const title = encodeURIComponent(event.title);
    const description = encodeURIComponent(stripHtml(event.description));
    const location = encodeURIComponent(event.location ?? '');

    // Parse dates -- when the API didn't supply a startDate we fall back to "now" so
    // the calendar link is still well-formed (the page should not surface this CTA
    // when the event has no schedule, but we never want to produce "Invalid Date").
    const startDate = event.startDate ? new Date(event.startDate) : new Date();
    const endDate = event.endDate ? new Date(event.endDate) : new Date(startDate.getTime() + 2 * 60 * 60 * 1000); // Default 2 hours

    if (event.isAllDayEvent) {
        // All-day events reach us pinned to UTC midnight by normalizeEvent, so
        // read the calendar day from UTC components (not local ones) and use
        // RFC 5545's exclusive end-date convention (the day AFTER the last day
        // of the event) so calendar apps render a full-day block instead of a
        // 2-hour slot shifted by the viewer's local offset.
        const startDay = formatUtcDateOnly(startDate);
        const lastDay = event.endDate ? endDate : startDate;
        const lastDayBasic = formatUtcDateOnly(lastDay);
        const exclusiveEndDay = formatUtcDateOnly(addUtcDays(lastDay, 1));
        const isMultiDay = lastDayBasic !== startDay;

        // Google and ICS both take the RFC 5545 exclusive end date.
        const google = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startDay}/${exclusiveEndDay}&details=${description}&location=${location}`;

        // Outlook is the exception twice over: for all-day events it wants the
        // extended `YYYY-MM-DD` form (it won't parse the basic one), and it
        // applies the exclusive-end adjustment itself, so it takes the
        // INCLUSIVE last day. Passing the exclusive end here adds a day.
        const outlook = `https://outlook.live.com/calendar/0/deeplink/compose?subject=${title}&startdt=${formatUtcDateOnly(startDate, '-')}&enddt=${formatUtcDateOnly(lastDay, '-')}&allday=true&body=${description}&location=${location}`;

        // Yahoo can't express a multi-day all-day event: `dur=allday` is the
        // only parameter that flips the all-day toggle, but Yahoo ignores
        // `dur` whenever `et` is present -- and a multi-day event needs `et`
        // to keep its end date. So single-day events get a true all-day entry,
        // and multi-day ones fall back to an inclusive st/et range that Yahoo
        // renders midnight-to-midnight: right dates, no all-day banner.
        const yahooRange = isMultiDay ? `st=${startDay}&et=${lastDayBasic}` : `st=${startDay}&dur=allday`;
        const yahoo = `https://calendar.yahoo.com/?v=60&title=${title}&${yahooRange}&desc=${description}&in_loc=${location}`;

        const icsContent = [
            'BEGIN:VCALENDAR',
            'VERSION:2.0',
            'BEGIN:VEVENT',
            `DTSTART;VALUE=DATE:${startDay}`,
            `DTEND;VALUE=DATE:${exclusiveEndDay}`,
            `SUMMARY:${event.title}`,
            `DESCRIPTION:${stripHtml(event.description)}`,
            `LOCATION:${event.location ?? ''}`,
            'END:VEVENT',
            'END:VCALENDAR'
        ].join('\n');
        const apple = `data:text/calendar;charset=utf-8,${encodeURIComponent(icsContent)}`;

        return { google, apple, outlook, yahoo };
    }

    // Format for Google Calendar (YYYYMMDDTHHmmssZ)
    const formatGoogleDate = (date: Date) => {
        return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
    };
    
    const googleStart = formatGoogleDate(startDate);
    const googleEnd = formatGoogleDate(endDate);
    
    // Format for Yahoo (YYYYMMDDTHHmmss)
    const formatYahooDate = (date: Date) => {
        return date.toISOString().replace(/[-:]/g, '').split('.')[0];
    };
    
    // Calculate duration in hours and minutes for Yahoo
    const durationMs = endDate.getTime() - startDate.getTime();
    const durationHours = Math.floor(durationMs / (1000 * 60 * 60));
    const durationMinutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    const yahooDuration = `${String(durationHours).padStart(2, '0')}${String(durationMinutes).padStart(2, '0')}`;
    
    const google = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${googleStart}/${googleEnd}&details=${description}&location=${location}`;
    
    const outlook = `https://outlook.live.com/calendar/0/deeplink/compose?subject=${title}&startdt=${startDate.toISOString()}&enddt=${endDate.toISOString()}&body=${description}&location=${location}`;
    
    const yahoo = `https://calendar.yahoo.com/?v=60&title=${title}&st=${formatYahooDate(startDate)}&dur=${yahooDuration}&desc=${description}&in_loc=${location}`;
    
    // Generate ICS content for Apple Calendar
    const icsContent = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'BEGIN:VEVENT',
        `DTSTART:${formatGoogleDate(startDate)}`,
        `DTEND:${formatGoogleDate(endDate)}`,
        `SUMMARY:${event.title}`,
        `DESCRIPTION:${stripHtml(event.description)}`,
        `LOCATION:${event.location ?? ''}`,
        'END:VEVENT',
        'END:VCALENDAR'
    ].join('\n');
    
    const apple = `data:text/calendar;charset=utf-8,${encodeURIComponent(icsContent)}`;
    
    return { google, apple, outlook, yahoo };
}

/**
 * Tracked share URLs for an event's confirmation page. Mirrors the equivalent
 * Petitions surface (`getPetitionShareUrls`) so the Action Center reports can
 * attribute traffic back to a specific user + medium. The returned URLs carry
 * a per-share token created by the backend's UserShareFactory<Share>; opening
 * them lands on the event's Register page with the originating user trackable
 * in the activity log.
 *
 * Calls the legacy UserSite endpoint (`/Api/Events/{id}/ShareUrls`) -- the new
 * REST host hasn't picked up share-tracking dependencies yet. Vite proxies
 * this path to the remote dev environment via a regex rule that takes
 * precedence over the local-REST `/Api/events` route (see vite.config.ts).
 *
 * Returns null on failure so callers can fall back to the un-tracked share
 * URL constructed client-side. Anonymous calls (no `userToken`) still work --
 * they just produce a tracked URL with no `user` attribution.
 */
export interface EventShareUrls {
    webShareUrl: string;
    facebookShareUrl: string;
    twitterShareUrl: string;
    linkedInShareUrl: string;
}

export const getEventShareUrls = async (
    eventId: number,
    userToken?: string,
    referringNugget?: string
): Promise<EventShareUrls | null> => {
    try {
        const params = new URLSearchParams({ association: associationIdName });
        if (siteNumber) params.set('siteNumber', String(siteNumber));
        if (userToken) params.set('userToken', userToken);
        if (referringNugget) params.set('vvsn', referringNugget);
        const url = `events/${eventId}/shareurls?${params.toString()}`;
        return await fetchGet<EventShareUrls>(url);
    } catch {
        return null;
    }
};

/**
 * Get the active events for the current association, split into spotlight vs.
 * other groups.
 *
 * Both backends now return a flat Event[] in camelCase, which we group
 * client-side while respecting the server-stamped isRegistered/priority.
 *
 * The grouped-object branch below handles a { spotlightEvents, otherEvents }
 * envelope that an older REST build returns. That envelope was reverted for
 * wire parity with legacy (see routes/Events/AGENTS.md), but test servers can
 * still be running the build that emits it, and the cost of tolerating it is
 * three lines.
 *
 * Both shapes then go through {@link orderEventGroups}, so the card order a
 * user sees does not depend on which backend answered.
 *
 * Identity handling: callers may pass a userToken (typically the remembered /
 * single-sign-on user's URL token). When supplied, it is forwarded as a `user`
 * query parameter so the server can stamp `isRegistered` on each event in a
 * single batch query (see EventConverter.ConvertSearchResponse(user)).
 *
 * This is cheaper than Petitions' approach of N parallel /signaturestatus
 * calls because the REST Events endpoint was built to accept the token on the
 * list call directly and batch-load registrations in one DB hit. Petitions can
 * adopt this pattern too when its REST migration lands.
 *
 * If no token is provided (or it's blank) the call stays anonymous and the
 * `isRegistered` flag will be false on every card -- the same behaviour as
 * before this parameter existed.
 */
export async function getEvents(userToken?: string): Promise<EventsResponse> {
    const mock = getMockEvents();
    if (mock) return mock;

    const token = userToken?.trim();
    const baseQuery = buildAssociationSiteQuery();
    const query = token ? `${baseQuery}&user=${encodeURIComponent(token)}` : baseQuery;
    const url = `events?${query}`;
    const response = await fetchGet<unknown>(url);

    if (response && typeof response === 'object' && !Array.isArray(response)) {
        const split = response as {
            SpotlightEvents?: unknown[]; OtherEvents?: unknown[];
            spotlightEvents?: unknown[]; otherEvents?: unknown[];
        };
        return orderEventGroups(
            (split.SpotlightEvents ?? split.spotlightEvents ?? []).map(normalizeEvent),
            (split.OtherEvents ?? split.otherEvents ?? []).map(normalizeEvent)
        );
    }

    const events: Event[] = ((response as unknown[]) ?? []).map(normalizeEvent);

    return orderEventGroups(
        events.filter(e => e.spotlighted),
        events.filter(e => !e.spotlighted)
    );
}

const eventStartTime = (event: Event): number =>
    event.startDate ? new Date(event.startDate).getTime() : Infinity;

/**
 * Puts the two list groups in display order: spotlight events by admin priority
 * (highest first) then soonest start, other events by soonest start. Events with
 * no start date sort last.
 *
 * Applied to every backend shape on purpose. The legacy list endpoint returns a
 * flat, unordered array; REST sorts server-side in
 * `EventConverter.ConvertSearchResponse`, but a client running against an older
 * REST build would still get source order. Ordering here keeps the rendered
 * list identical across backends and across the cutover.
 */
function orderEventGroups(spotlightEvents: Event[], otherEvents: Event[]): EventsResponse {
    spotlightEvents.sort((a, b) => {
        const priorityDelta = (b.priority ?? 0) - (a.priority ?? 0);
        if (priorityDelta !== 0) return priorityDelta;
        return eventStartTime(a) - eventStartTime(b);
    });
    otherEvents.sort((a, b) => eventStartTime(a) - eventStartTime(b));

    return { spotlightEvents, otherEvents };
}

/**
 * Whether new registrations should be blocked for this event.
 * Prefers the server-computed flag; falls back to max/count when absent.
 */
export function isEventRegistrationFull(
    event: Pick<Event, 'isRegistrationFull' | 'maxRegistrations' | 'registrationCount'> | null | undefined
): boolean {
    if (!event) return false;
    if (event.isRegistrationFull) return true;
    if (event.maxRegistrations == null || event.maxRegistrations <= 0) return false;
    return (event.registrationCount ?? 0) >= event.maxRegistrations;
}

/**
 * Prefer a positive numeric id from an event DTO or an explicit fallback (route param).
 * Petitions don't need this helper: REST returns a single reliable `id` via
 * `mapPetitionResponseToPetition`. Events tolerate legacy payloads with `eventId`
 * and erroneous `id: 0` (see normalizeEvent).
 */
export function resolveEventId(event: Pick<Event, 'id' | 'eventId'> | null | undefined, fallback?: number): number {
    const fromEvent = event?.id || event?.eventId;
    if (fromEvent && fromEvent > 0) return fromEvent;
    if (fallback && fallback > 0) return fallback;
    return 0;
}

/**
 * Fetch a single event. Pass `userToken` so legacy/REST can stamp `isRegistered`
 * (same as the list endpoint).
 */
export async function getEventById(id: number, userToken?: string): Promise<Event> {
    const mockEvent = findMockEvent(id);
    if (mockEvent) return mockEvent;

    const token = userToken?.trim();
    const query = token
        ? `${buildAssociationSiteQuery()}&user=${encodeURIComponent(token)}`
        : buildAssociationSiteQuery();
    const url = `events/${id}?${query}`;
    const response = await fetchGet<Event | Event[]>(url);
    const raw = Array.isArray(response) ? response[0] : response;
    const normalized = normalizeEvent(raw);
    const resolvedId = resolveEventId(normalized, id);
    return { ...normalized, id: resolvedId, eventId: resolvedId };
}

/**
 * Fetch the current user's existing registration for an event (if any). The
 * endpoint accepts a ?user={token} query param; we only send it when we have a
 * non-empty token. Anonymous calls return the "no registration" state, in
 * which case the RegisterPage falls back to vvSettings.userContainer.userModel
 * for form prefill -- same pattern Petitions uses (where there is no parallel
 * fetch at all; prefill is purely client-side from vvSettings).
 */
/**
 * Map GET …/Registration JSON (EventRegistrationContract) into the flat shape
 * RegisterPage expects. Without this, phone/address fields stay empty even when
 * the server returned homeAddress.phoneNumber, etc.
 */
export function normalizeEventRegistration(
    raw: unknown,
    eventId: number,
    event: Event
): EventRegistration {
    const empty: EventRegistration = {
        eventId,
        event,
        firstName: '',
        lastName: '',
        email: '',
        preferredContactMethod: 'email',
        firstTimeAttendee: false,
        isRegistered: event.isRegistered ?? false
    };
    if (!raw || typeof raw !== 'object') return empty;

    const r = raw as Record<string, unknown>;
    const pick = <T>(...keys: string[]): T | undefined => {
        for (const key of keys) {
            if (r[key] !== undefined) return r[key] as T;
        }
        return undefined;
    };

    const home = pick<Record<string, unknown>>('homeAddress', 'HomeAddress');
    const business = pick<Record<string, unknown>>('businessAddress', 'BusinessAddress');
    const addrField = (addr: Record<string, unknown> | undefined, camel: string, pascal: string): string | undefined => {
        if (!addr) return undefined;
        const v = addr[camel] !== undefined ? addr[camel] : addr[pascal];
        return typeof v === 'string' ? v : undefined;
    };

    const canceled = Boolean(pick<boolean>('canceled', 'Canceled'));
    const registrationDate = pick<string>('registrationDate', 'RegistrationDate');
    const hasActiveRegistration = Boolean(registrationDate?.trim()) && !canceled;

    const preferredRaw = pick<string>('preferredContactMethod', 'PreferredContactMethod');
    let preferredContactMethod: EventRegistration['preferredContactMethod'] = 'email';
    if (preferredRaw) {
        const lower = preferredRaw.toLowerCase();
        if (lower.includes('cell') || lower.includes('phone')) preferredContactMethod = 'phone';
        else if (lower === 'text') preferredContactMethod = 'phone';
        else preferredContactMethod = 'email';
    }

    return {
        eventId,
        event,
        firstName: (pick<string>('firstName', 'FirstName') ?? '') as string,
        lastName: (pick<string>('lastName', 'LastName') ?? '') as string,
        email: (pick<string>('email', 'Email') ?? '') as string,
        phone: pick<string>('phoneNumber', 'PhoneNumber'),
        mobileNumber: pick<string>('cellPhoneNumber', 'CellPhoneNumber'),
        fax: pick<string>('faxNumber', 'FaxNumber'),
        address: addrField(home, 'streetAddress', 'StreetAddress'),
        city: addrField(home, 'city', 'City'),
        state: addrField(home, 'state', 'State'),
        zip: addrField(home, 'zipCode', 'ZipCode'),
        businessStreet: addrField(business, 'streetAddress', 'StreetAddress'),
        businessCity: addrField(business, 'city', 'City'),
        businessState: addrField(business, 'state', 'State'),
        businessZip: addrField(business, 'zipCode', 'ZipCode'),
        businessName: pick<string>('companyName', 'CompanyName'),
        businessTitle: pick<string>('companyTitle', 'CompanyTitle'),
        preferredContactMethod,
        specialNeeds: pick<string>('specialNeeds', 'SpecialNeeds'),
        notes: pick<string>('notes', 'Notes'),
        firstTimeAttendee: Boolean(pick<boolean>('firstTimeAttendee', 'FirstTimeAttendee')),
        isRegistered: hasActiveRegistration || (event.isRegistered ?? false)
    };
}

export async function getEventRegistration(
    eventId: number,
    userToken?: string,
    event?: Event
): Promise<EventRegistration> {
    const mockEvent = findMockEvent(eventId);
    if (mockEvent) {
        return normalizeEventRegistration(null, eventId, mockEvent);
    }

    const token = userToken?.trim() || resolveRegistrationUserToken({ userToken }, userToken);
    const url = `${EVENTS_MUTATION_PREFIX}/${eventId}/Registration?${appendUserToQuery(buildAssociationSiteQuery(), token)}`;
    const raw = await fetchGet<unknown>(url);
    const resolvedEvent = event ?? await getEventById(eventId, token);
    return normalizeEventRegistration(raw, eventId, resolvedEvent);
}

/**
 * Submit an event registration.
 *
 * This stays on **legacy UserSite** `EventsController.Registration` by design:
 * the IIS rewrite deliberately sends only the Events list and detail paths to
 * RestApi, because RestApi's registration actions skip the confirmation and
 * admin emails, activity logging and validation that this one performs.
 * That action authenticates exclusively
 * from the `?user={token}` query string — same as
 * `VoterVoiceAPI.saveEventRegistration`. Omitting `user` yields **404** even
 * when `userToken` is in the POST body.
 *
 * REST (when routed) accepts `user` on the query and/or `userToken` / `userId`
 * in the body via `ResolveUserForRegistration`.
 */
export async function registerForEvent(request: RegisterEventRequest, userToken?: string): Promise<EventRegistrationContract> {
    if (getMockEvents()) {
        // Pretend the server accepted the registration so the confirmation flow can
        // be exercised end-to-end without a backend round trip.
        return {
            success: true,
            message: 'Mock registration accepted.'
        };
    }

    const token = resolveRegistrationUserToken(request, userToken);
    if (!token) {
        throw new Error('User token is required to register for an event');
    }

    const url = `${EVENTS_MUTATION_PREFIX}/${request.eventId}/Registration?${appendUserToQuery(buildAssociationSiteQuery(), token)}`;
    const body = buildRegistrationPostBody(request);

    // fetchPost throws ApiResponseError on any non-2xx status, so if we reach the
    // line below the server already persisted the registration. The REST
    // controller responds 201 Created with the saved EventRegistrationContract DTO
    // (server-side shape: EventId, UserId, FirstName, ...). That shape does not
    // include a `success` flag -- our client-facing EventRegistrationContract is
    // a UI projection. We translate "no exception" into success: true so callers
    // (RegisterPage handleSubmit) get the simple success/message contract they
    // already check against, matching Petitions' signPetition() behaviour.
    await fetchPost<Record<string, unknown>, unknown>(url, body);
    return { success: true };
}

/**
 * Cancel registration. Legacy UserSite requires `?user={token}` (see VoterVoiceAPI.js).
 */
export async function cancelEventRegistration(eventId: number, userToken?: string): Promise<{ success: boolean; message?: string }> {
    if (getMockEvents()) {
        return { success: true, message: 'Mock unregister accepted.' };
    }

    const token = resolveRegistrationUserToken({ userToken }, userToken);
    if (!token) {
        throw new Error('User token is required to cancel event registration');
    }

    const url = `${EVENTS_MUTATION_PREFIX}/${eventId}/CancelRegistration?${appendUserToQuery(buildAssociationSiteQuery(), token)}`;
    // As with registerForEvent, fetchPost throws on non-2xx -- reaching the
    // return below means the cancellation persisted. The REST controller
    // responds 204 No Content; we synthesise the client-side success shape.
    await fetchPost<Record<string, unknown>, unknown>(url, {});
    return { success: true };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Coerces an API event payload into the shape the Svelte components expect.
 *
 * Accepts these casing styles:
 *   - The current contract, identical on both backends: camelCase (`id`,
 *     `eventId`, `spotlighted`, `eventTimeZone`, `isAllDayEvent`,
 *     `isRegistered`, ...). REST's `EventContract` is named to match the legacy
 *     payload field for field and ASP.NET Core camelCases it on the wire.
 *   - The short-lived REST spellings `isSpotlight`, `timeZone` and `isAllDay`,
 *     from the clean-slate contract that predated the parity fix (see
 *     routes/Events/AGENTS.md). Still accepted because a test server can be
 *     running that build; dropping a spelling silently loses the field, which
 *     reads downstream as "timed event in the viewer's local zone".
 *   - PascalCase keys (`Id`, `Title`, `StartDate`, ...). These are vestigial:
 *     ASP.NET Core camelCases the contract on the wire, so no backend has ever
 *     actually sent them. Harmless to keep and not worth a separate change.
 *
 * Neither backend sends `eventDisplayDate` -- it is built by EventModelBuilder
 * for the Razor views only -- so {@link formatEventScheduleDisplay} always
 * formats from startDate/endDate here and its preset branch stays dormant.
 *
 * We index into the raw payload by every known key, first match wins, and fall
 * back to sensible defaults so downstream code can keep speaking the Svelte
 * camelCase vocabulary regardless of which backend answered.
 *
 * Will cleanup the code later after migration to REST API
 */
function normalizeEvent(raw: unknown): Event {
    const empty = { id: 0, title: '', description: '', isRegistered: false } as Event;
    if (!raw || typeof raw !== 'object') return empty;

    const r = raw as Record<string, unknown>;
    const pick = <T>(...keys: string[]): T | undefined => {
        for (const key of keys) {
            if (r[key] !== undefined) return r[key] as T;
        }
        return undefined;
    };

    // Use || not ?? — some legacy payloads include `id: 0` (default int) alongside a
    // valid `eventId`, and `0 ?? 8005` incorrectly keeps zero (breaking registration).
    const id = (
        pick<number>('id', 'Id')
        || pick<number>('eventId', 'EventId')
        || 0
    ) as number;
    const spotlighted = (pick<boolean>('spotlighted', 'IsSpotlight') ?? pick<boolean>('isSpotlight', 'IsSpotlight') ?? false) as boolean;
    const isAllDayEvent = pick<boolean>('isAllDayEvent', 'IsAllDay', 'isAllDay');
    const startDate = pick<string>('startDate', 'StartDate');
    const endDate = pick<string>('endDate', 'EndDate');

    return {
        id,
        eventId: id,
        title: (pick<string>('title', 'Title') ?? '') as string,
        description: (pick<string>('description', 'Description') ?? '') as string,
        alert: pick<string>('alert', 'Alert'),
        eventDisplayDate: pick<string>('eventDisplayDate', 'EventDisplayDate'),
        // All-day dates are normalised to UTC midnight so every consumer can
        // read them as a calendar day; timed dates are real instants and are
        // passed through untouched. See {@link toUtcCalendarDay}.
        startDate: isAllDayEvent ? toUtcCalendarDay(startDate) : startDate,
        endDate: isAllDayEvent ? toUtcCalendarDay(endDate) : endDate,
        eventTimeZone: pick<string>('eventTimeZone', 'TimeZone', 'timeZone'),
        isAllDayEvent,
        location: pick<string>('location', 'Location'),
        maxRegistrations: pick<number>('maxRegistrations', 'MaxRegistrations'),
        registrationCount: pick<number>('registrationCount', 'RegistrationCount'),
        isRegistrationFull: pick<boolean>('isRegistrationFull', 'IsRegistrationFull'),
        availableSlots: pick<number>('availableSlots', 'AvailableSlots'),
        imageUrl: pick<string>('imageUrl', 'ImageUrl'),
        spotlighted,
        isSpotlight: spotlighted,
        priority: pick<number>('priority', 'Priority'),
        hideAlert: pick<boolean>('hideAlert', 'HideAlert'),
        isRegistered: (pick<boolean>('isRegistered', 'IsRegistered') ?? false) as boolean,
        accommodations: pick<string>('accommodations', 'Accommodations'),
        travelDetails: pick<string>('travelDetails', 'TravelDetails'),
        program: pick<string>('program', 'Program'),
        resources: pick<string>('resources', 'Resources'),
        showOtherRegistrationInformation: pick<boolean>('showOtherRegistrationInformation', 'ShowOtherRegistrationInformation'),
        thankYouMessage: pick<string>('thankYouMessage', 'ThankYouMessage'),
        sharingEnabled: pick<boolean>('sharingEnabled', 'SharingEnabled'),
    };
}

